
import {
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useState,
    useRef
} from "react";
import { faAsterisk } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Col, Form, Row } from "react-bootstrap";
import { Option } from "react-bootstrap-typeahead/types/types";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, AppState } from "store/configureStore";
import TypeaheadWithIcon from "pages/UI/TypeaheadWithIcon";

import BankAccount from "entities/BankAccount";
import { AddressRecord, ContactRecord, PersonalContacts } from "entities/CommunicationInfo";
import PersonalInfo from "entities/PersonalInfo";
import WorkerDocument from "entities/WorkerDocument";
import RelatedPerson from "entities/relatedPersons/RelatedPerson";
import CommunicationValueType from "util/config/communicationValueType";

import ChangeBankAccount from "../../Employee/Components/BankDetails/ChangeBankAccount";
import ChangeContactAddress from "../Components/Contacts/ChangeContactAddress";
import ChangeDocument from "../Components/Documents/ChangeDocument";
import ContactPersonModal from "../Components/Contacts/ContactPersonModal";
import ProfileDataNode from "../Components/ProfileDataNode";
import ProfileEditContentNode from "../Components/ProfileEditContentNode";
import ProfileEditNode from "../Components/ProfileEditNode";
import ChangePhoto from "../Components/WorkerDetailComponents/ChangePhoto";
import PhotoInfo from "../Components/WorkerDetailComponents/PhotoInfo";
import ProfileImage from "../Components/WorkerDetailComponents/ProfileImage";

import { isEmailValid, isPhoneValid } from "components/validation";
import { getCountries } from "store/slices/address";
import { changeWorkerPersonalContacts, getWorkerCommunicationInfo } from "store/slices/communicationInfo";
import { deleteRelatedPerson, getWorkerRelatedPersons } from "store/slices/relatedPersons";
import { changeWorkerPersonalInfo, getWorkerDetails } from "store/slices/workerDetails";


interface Props {
    isInEditMode?: boolean;
    onSubmit: () => void;
}

interface FormState {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    address: AddressRecord,
    bankAccount: BankAccount,
    contactPerson?: RelatedPerson,
    documents: WorkerDocument[],
    citizenshipCountryId: number | null
}

interface FieldValidation {
    valid: boolean;
    touched: boolean;
    errorMessage: string;
}

type ValidationState = {
    [key in keyof FormState]?: FieldValidation;
};

const PersonalInfoCard = ({ isInEditMode, onSubmit }: Props) => {
    const { t, i18n } = useTranslation(["parts"]);

    const dispatch = useDispatch<AppDispatch>();

    const { profileImage, data: workerInfo, loading: workerInfoLoading } = useSelector(
        (state: AppState) => state.entities.workerDetails.forRequesting
    );

    const { list: contacts, loading: contactsLoading } = useSelector(
        (state: AppState) => state.entities.communicationInfo.forRequesting
    );

    const { list: relatedPersons, loading: contactPersonLoading, lastFetch: relatedPersonsLastFetch } = useSelector
        ((state: AppState) => state.entities.relatedPersons.workerRelatedPersons);

    const { reloadData: relatedPersonsPost } = useSelector((state: AppState) => state.entities.relatedPersons);

    const { loading: postLoading } = useSelector((state: AppState) => state.entities.communicationInfo.forPosting);

    const { list: countries } = useSelector((state: AppState) => state.entities.address.countries);

    const reloadData = postLoading || relatedPersonsPost;

    const loadData = workerInfoLoading || contactsLoading || contactPersonLoading || relatedPersonsPost || postLoading;

    const personalContacts = useMemo(() => {
        const contact = contacts.find(
            c => c.Type === CommunicationValueType.Personal
        );
        return contact;
    }, [contacts]);

    const person: RelatedPerson | undefined = useMemo(() => {

        return relatedPersons.find(p => p.IsEmergencyContact);
    }, [relatedPersons]);

    const [formData, setFormData] = useState<FormState>({
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
        address: { FullAddress: "" } as AddressRecord,
        bankAccount: { IBAN: "", BankName: "" } as BankAccount,
        contactPerson: undefined,
        documents: [],
        citizenshipCountryId: null,
    });

    const [validation, setValidation] = useState<ValidationState>({
        firstName: { valid: true, touched: false, errorMessage: "" },
        lastName: { valid: true, touched: false, errorMessage: "" },
        email: { valid: true, touched: false, errorMessage: "" },
        phone: { valid: true, touched: false, errorMessage: "" },
        address: { valid: true, touched: false, errorMessage: "" },
        bankAccount: { valid: true, touched: false, errorMessage: "" },
        contactPerson: { valid: true, touched: false, errorMessage: "" },
        citizenshipCountryId: { valid: false, touched: false, errorMessage: "" },
        documents: { valid: false, touched: false, errorMessage: "" },
    });

    const [showChangePhotoModal, setShowChangePhotoModal] = useState(false);
    const [showPhotoInfoModal, setShowPhotoInfoModal] = useState(false);

    const initialDataLoaded = useRef(false);

    useEffect(() => {
        if (isInEditMode && initialDataLoaded.current) {
            return;
        }

        if (!isInEditMode) {
            initialDataLoaded.current = false;
        }

        // Mark as loaded once workerInfo, personalContacts, AND the relatedPersons
        // fetch have all completed — regardless of whether a contact person exists.
        // Using relatedPersonsLastFetch (instead of `person`) prevents the form from
        // being reset when a new contact person is saved and the store refreshes.
        if (workerInfo && personalContacts && relatedPersonsLastFetch !== null) {
            initialDataLoaded.current = true;
        }

        const newValues: FormState = {
            firstName: workerInfo?.FirstName || "",
            lastName: workerInfo?.LastName || "",
            phone: personalContacts?.Phone?.Value || "",
            email: personalContacts?.Email?.Value || "",
            bankAccount: workerInfo?.BankAccounts?.length ? workerInfo?.BankAccounts![0] : { IBAN: "", BankName: "" } as BankAccount,
            address: personalContacts?.Addresses?.length ? personalContacts?.Addresses![0] : { FullAddress: "" } as AddressRecord,
            contactPerson: person,
            documents: workerInfo?.Documents ? workerInfo.Documents.map(d => ({ ...d })) : [],
            citizenshipCountryId: workerInfo?.CitizenshipCountryId ?? null
        };
        setFormData(newValues);
        const newValidation: ValidationState = {};
        for (const field of Object.keys(newValues) as Array<keyof FormState>) {
            newValidation[field] = validateField(field, newValues[field], true);
        }
        setValidation(newValidation);
    }, [workerInfo, personalContacts, person, isInEditMode, relatedPersonsLastFetch]);

    const validateField = (
        field: keyof FormState,
        value: any,
        dontTouch: boolean = false
    ): FieldValidation => {
        let valid = true;
        let errorMessage = "";

        switch (field) {
            case "firstName":
            case "lastName":
                valid = typeof value === "string" && !!value.match(/^[a-zA-ZÄÖÜÕäöüõŠšŽž\s'-]+$/u);
                errorMessage = '';
                break;

            case "email":
                valid = typeof value === "string" && isEmailValid(value) && !value.endsWith(".");
                errorMessage = !valid && (typeof value === "string" && value.length > 3) ? t("common:form.errors.invalidEmail") : "";
                break;

            case "phone":
                valid = typeof value === "string" && isPhoneValid(value) && !value.endsWith(".");
                errorMessage = !valid && (typeof value === "string" && value.length > 3) ? t("common:form.errors.invalidPhone") : "";
                break;

            case "address":
                valid = !!value?.FullAddress && value.FullAddress.trim().length > 0;
                errorMessage = '';
                break;

            case "bankAccount":
                valid = !!value?.IBAN && value.IBAN.trim().length > 0;
                errorMessage = '';
                break;

            case "citizenshipCountryId":
                valid = value !== null && value !== undefined;
                errorMessage = '';
                break;

            case "documents":
                valid = Array.isArray(value) && value.length > 0;
                errorMessage = '';
                break;

            default:
                valid = true;
                errorMessage = "";
        }

        return { valid, touched: !dontTouch, errorMessage };
    };
    const getErrorMessage = (field: keyof FormState): string => {
        const fieldValidation = validation[field];
        return fieldValidation && fieldValidation.touched ? fieldValidation.errorMessage : "";
    };


    const handleInputChange = useCallback(
        (field: keyof FormState, value: any) => {
            setFormData(prev => ({ ...prev, [field]: value }));
            const newValidation = validateField(field, value);
            setValidation(prev => ({ ...prev, [field]: newValidation }));
        },
        []
    );

    useEffect(() => {
        if (!reloadData) {
            dispatch(getWorkerCommunicationInfo());
            dispatch(getWorkerDetails());
            dispatch(getWorkerRelatedPersons(false));
            dispatch(getCountries());
        }
    }, [reloadData, dispatch]);


    const openChangePhotoModal = () => {
        setShowChangePhotoModal(true);
    };

    const openPhotoInfoModal = () => {
        setShowPhotoInfoModal(true);
    };

    const handleChangePhotoSubmit = () => {
        setShowChangePhotoModal(false);
    };

    const handleSavePersonalInfo = async () => {
        // Save logic here
        const updatedPersonalInfo: PersonalInfo = {
            WorkerId: 0, // will be set to correct Id in store
            FirstName: formData.firstName,
            LastName: formData.lastName,
            WorkPhone: formData.phone,
            WorkEmail: formData.email,
            BirthdayVisibleToCoworkers: false,
            CommunicationLanguageId: 2,
            CitizenshipCountryId: formData.citizenshipCountryId,
            Documents: formData.documents
        };

        const contacts: PersonalContacts = {
            WorkerId: 0,
            Email: { Id: personalContacts?.Email?.Id ?? 0, Value: formData.email, IsPrivate: true, IsPrimary: true } as ContactRecord,
            Phone: { Id: personalContacts?.Phone?.Id ?? 0, Value: formData.phone, IsPrivate: true, IsPrimary: true } as ContactRecord,
            Addresses: [{ ...formData.address, Id: personalContacts?.Addresses?.length ? personalContacts?.Addresses![0].Id : 0 } as AddressRecord],
            BankAccounts: [{ ...formData.bankAccount, BankAccountId: workerInfo?.BankAccounts?.length ? workerInfo?.BankAccounts![0].BankAccountId : 0 } as BankAccount]
        }
        await dispatch(changeWorkerPersonalInfo(updatedPersonalInfo));
        await dispatch(changeWorkerPersonalContacts(contacts));
        onSubmit();
    };

    const [showAddressModal, setShowAddressModal] = useState<boolean>(false);
    const [showAccountModal, setShowAccountModal] = useState<boolean>(false);
    const [showContactPersonModal, setShowContactPersonModal] = useState<boolean>(false);
    const [showDocumentModal, setShowDocumentModal] = useState<boolean>(false);
    const [editingDocumentIndex, setEditingDocumentIndex] = useState<number | null>(null);

    const openAddDocumentModal = () => {
        setEditingDocumentIndex(null);
        setShowDocumentModal(true);
    };

    const openEditDocumentModal = (index: number) => {
        setEditingDocumentIndex(index);
        setShowDocumentModal(true);
    };

    const handleDocumentSubmit = (data: WorkerDocument) => {
        setFormData(prev => {
            const documents = [...prev.documents];
            if (editingDocumentIndex !== null) {
                documents[editingDocumentIndex] = data;
            } else {
                documents.push(data);
            }
            setValidation(prevValidation => ({ ...prevValidation, documents: validateField("documents", documents) }));
            return { ...prev, documents };
        });
        setShowDocumentModal(false);
        setEditingDocumentIndex(null);
    };

    const handleClearDocument = (index: number) => {
        setFormData(prev => {
            const documents = prev.documents.filter((_, i) => i !== index);
            setValidation(prevValidation => ({ ...prevValidation, documents: validateField("documents", documents) }));
            return { ...prev, documents };
        });
    };

    const formatDocumentValidTo = (value?: Date | string | null): string => {
        if (!value) {
            return "";
        }
        const date = value instanceof Date ? value : new Date(value);
        return isNaN(date.getTime()) ? "" : date.toLocaleDateString(i18n.language);
    };

    const handleAddressSubmit = (data: AddressRecord) => {

        const match = data && data.FullAddress && data.FullAddress.match(/, ([A-Z]{3})$/);

        if (data && data.FullAddress && !match) {
            data.FullAddress = data.FullAddress + ", " + data.CountryCode;
        }

        setFormData(prev => ({ ...prev, address: data }));
        setValidation(prev => ({ ...prev, address: validateField("address", data) }));
        setShowAddressModal(false);
    };

    const handleBankAccountSubmit = (data: BankAccount) => {

        setFormData(prev => ({ ...prev, bankAccount: data }));
        setValidation(prev => ({ ...prev, bankAccount: validateField("bankAccount", data) }));
        setShowAccountModal(false);
    };

    const handleContactPersonSubmit = (data: RelatedPerson | undefined) => {
        setFormData(prevState => ({
            ...prevState,
            contactPerson: data
        }));
        setShowContactPersonModal(false);
    };

    const handleClearContactPerson = async () => {
        if (formData.contactPerson?.Id) {
            try {
                await dispatch(deleteRelatedPerson(formData.contactPerson.Id));
            } catch (error) {
                console.error("Failed to delete related person:", error);
            }
        }
        handleContactPersonSubmit(undefined);
    };

    const renderAddressContent = useCallback(
        (): ReactNode | string => {

            if (formData.address?.FullAddress) {
                return formData.address.FullAddress;
            }
            if (isInEditMode) {
                return <div className="form-control-placeholder">{t("common:form.placeholders.enterData")}</div>;
            }
            return "-";
        },
        [formData.address?.FullAddress, isInEditMode]
    );

    const renderBankAccountContent = useCallback(
        (): ReactNode | string => {
            return formData.bankAccount?.IBAN ? (
                <>
                    <div>{formData.bankAccount.BankName}</div>
                    <div>{formData.bankAccount.IBAN}</div>
                </>
            ) : (
                isInEditMode ? (
                    <div className="form-control-placeholder">{t("common:form.placeholders.enterData")}</div>
                ) : (
                    "-"
                )
            )
        },
        [formData.bankAccount, isInEditMode]
    );

    const citizenshipOptions = useMemo(
        () => countries
            .filter(c => typeof c.Name === "string" && c.Name.trim() !== "")
            .map(c => ({ id: c.Id, label: c.Name })),
        [countries]
    );

    const selectedCitizenship = useMemo(
        () => citizenshipOptions.filter(o => o.id === formData.citizenshipCountryId),
        [citizenshipOptions, formData.citizenshipCountryId]
    );

    const citizenshipCountryName = useMemo(
        () => countries.find(c => c.Id === formData.citizenshipCountryId)?.Name,
        [countries, formData.citizenshipCountryId]
    );

    const handleCitizenshipChange = useCallback(
        (selected: Option[]) => {
            const option = selected[0] as { id: number } | undefined;
            handleInputChange("citizenshipCountryId", option ? option.id : null);
        },
        [handleInputChange]
    );

    const renderContactPersonContent = useCallback(
        (): ReactNode | string => {
            return (formData.contactPerson && formData.contactPerson.Name) ? (
                <>
                    <div>{formData.contactPerson.Name}</div>
                    <div>{formData.contactPerson.Phone}</div>
                    <div>{formData.contactPerson.Email}</div>
                </>
            ) : (
                isInEditMode ? (
                    <div className="form-control-placeholder">{t("common:form.placeholders.enterContactPersonInfo")}</div>
                ) : (
                    "-"
                )
            )
        },
        [formData.contactPerson, isInEditMode]
    );

    return (
        <div className="card-body">
            {isInEditMode ? (
                <>
                    <Form>
                        <Row>
                            <Col className="col-12 col-xl-4 profile-image-edit-container d-flex justify-content-center ">
                                <div className="position-relative mb-4 d-flex flex-column align-items-center gap-2">
                                    <button
                                        type="button"
                                        className="profile-image-overlay-text d-flex flex-column align-items-center gap-2 border-0 bg-transparent p-2 rounded visual-selection-aid"
                                        onClick={openChangePhotoModal}
                                        aria-label={t("contacts.changePicture")}
                                    >
                                        <ProfileImage src={profileImage?.src} />
                                        <span className="fw-semibold">
                                            {t("contacts.changePicture")}
                                        </span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={openPhotoInfoModal}
                                        className="profile-image-overlay-text border-0 bg-transparent p-1 rounded visual-selection-aid"
                                        aria-label={t("contacts.photoRequirements.requirementsHeader")}
                                    >
                                        {t("contacts.photoRequirements.requirementsHeader")}
                                    </button>
                                </div>
                            </Col>
                            <Col className="col-12 col-xl-8">
                                <Row>
                                    <Col className="col-12 col-lg-6">
                                        <ProfileEditNode
                                            controlId="formFirstName"
                                            label={t("contacts.firstName")}
                                            value={formData.firstName}
                                            mandatory={true}
                                            placeholder={t("common:form.placeholders.enterData")}
                                            onChange={value => handleInputChange("firstName", value)}
                                            error={!validation.firstName?.valid ? getErrorMessage("firstName") : undefined}
                                        />
                                    </Col>
                                    <Col className="col-12 col-lg-6 position-relative">
                                        <ProfileEditNode
                                            controlId="formLastName"
                                            label={t("contacts.lastName")}
                                            value={formData.lastName}
                                            mandatory={true}
                                            placeholder={t("common:form.placeholders.enterData")}
                                            onChange={value => handleInputChange("lastName", value)}
                                            error={!validation.lastName?.valid ? getErrorMessage("lastName") : undefined}
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col className="col-12 col-lg-6">
                                        <ProfileDataNode
                                            label={t("contacts.identificationNumber")}
                                        >
                                            {workerInfo?.SSN}
                                        </ProfileDataNode>
                                    </Col>
                                    <Col className="col-12 col-lg-6">
                                        <ProfileEditNode
                                            controlId="formPhone"
                                            label={t("pages:profile.phone")}
                                            value={formData.phone || ""}
                                            mandatory={true}
                                            placeholder={t("common:form.placeholders.enterData")}
                                            onChange={value => handleInputChange("phone", value)}
                                            error={!validation.phone?.valid ? getErrorMessage("phone") : undefined}
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <ProfileEditNode
                                        controlId="formEmail"
                                        label={t("pages:profile.email")}
                                        value={formData.email || ""}
                                        mandatory={true}
                                        placeholder={t("common:form.placeholders.enterData")}
                                        onChange={value => handleInputChange("email", value)}
                                        error={!validation.email?.valid ? getErrorMessage("email") : undefined}
                                    />
                                </Row>
                                <Row>
                                    <ProfileEditContentNode
                                        label={t("pages:profile.homeAddress")}
                                        hideClearButton={false}
                                        mandatory={true}
                                        className={!validation.address?.valid ? "is-invalid" : ""}
                                        handleClear={() => { handleAddressSubmit({ FullAddress: "" } as AddressRecord); }}
                                    >
                                        <button 
                                            type="button"
                                            onClick={() => setShowAddressModal(true)} 
                                            className='form-control-input-custom w-100 text-start bg-transparent visual-selection-aid'
                                        >
                                            {renderAddressContent()}
                                        </button>
                                        {!validation.address?.valid && (
                                            <Form.Control.Feedback type="invalid">
                                                {getErrorMessage("address")}
                                            </Form.Control.Feedback>
                                        )}
                                    </ProfileEditContentNode>
                                </Row>
                                <Row>
                                    <ProfileEditContentNode
                                        label={t("pages:profile.bankAccount")}
                                        hideClearButton={false}
                                        mandatory={true}
                                        className={!validation.bankAccount?.valid ? "is-invalid" : ""}
                                        handleClear={() => { handleBankAccountSubmit({ IBAN: "", BankName: "" } as BankAccount); }}
                                    >
                                        <button 
                                            type="button"
                                            onClick={() => setShowAccountModal(true)} 
                                            className='form-control-input-custom w-100 text-start bg-transparent visual-selection-aid'
                                            aria-label={t("pages:profile.bankAccount")}
                                        >
                                            {renderBankAccountContent()}
                                        </button>
                                        {!validation.bankAccount?.valid && (
                                            <Form.Control.Feedback type="invalid">
                                                {getErrorMessage("bankAccount")}
                                            </Form.Control.Feedback>
                                        )}
                                    </ProfileEditContentNode>
                                </Row>
                                <Row>
                                    <div className='mb-4'>
                                        <div className="dataNode-label">
                                            {t("contacts.documents")}
                                            <span className="ms-1 text-danger"><FontAwesomeIcon icon={faAsterisk} /></span>
                                        </div>
                                        {formData.documents.map((document, index) => (
                                            <ProfileEditContentNode
                                                key={index}
                                                label={""}
                                                hideClearButton={false}
                                                handleClear={() => handleClearDocument(index)}
                                            >
                                                <button 
                                                    type="button"
                                                    onClick={() => openEditDocumentModal(index)} 
                                                    className="form-control-input-custom w-100 text-start bg-transparent visual-selection-aid"
                                                >
                                                    <div>{document.TypeName}</div>
                                                    <div>{document.Value}</div>
                                                    <div>
                                                        {formatDocumentValidTo(document.EndDate) ? `${formatDocumentValidTo(document.EndDate)}` : ""}
                                                    </div>
                                                </button>
                                            </ProfileEditContentNode>
                                        ))}
                                        <Button variant="link" className="w-auto px-0 visual-selection-aid" onClick={openAddDocumentModal}>
                                            {t("contacts.addDocument")}
                                        </Button>
                                    </div>
                                </Row>
                                <Row>
                                    <ProfileEditContentNode
                                        label={t("pages:profile.citizenship")}
                                        mandatory={true}
                                        hideClearButton={false}
                                        handleClear={() => handleInputChange("citizenshipCountryId", null)}
                                    >
                                        <TypeaheadWithIcon
                                            id="formCitizenship"
                                            onChange={handleCitizenshipChange}
                                            options={citizenshipOptions}
                                            selected={selectedCitizenship}
                                            placeholder={t("common:form.placeholders.enterData")}
                                            labelKey="label"
                                            className="form-control-input-custom visual-selection-aid"
                                        />
                                    </ProfileEditContentNode>
                                </Row>
                                <Row>
                                    <div className='mb-4'>
                                        <ProfileEditContentNode
                                            className="mb-0"
                                            label={t("parts:relatedContact.title")}
                                            hideClearButton={false}
                                            handleClear={handleClearContactPerson}
                                        >
                                            <button 
                                                type="button"
                                                onClick={() => setShowContactPersonModal(true)} 
                                                className="form-control-input-custom w-100 text-start bg-transparent visual-selection-aid"
                                            >
                                                {renderContactPersonContent()}
                                            </button>
                                        </ProfileEditContentNode>
                                        <div className="fs-6">{t("candidate_portal:personalInfo.contactPersonSharingTip")}</div>
                                    </div>
                                </Row>
                                <Row>
                                    <Button
                                        className="w-auto visual-selection-aid"
                                        variant="primary"
                                        disabled={Object.values(validation).some(v => !v?.valid)}
                                        onClick={handleSavePersonalInfo}
                                        aria-label={t("candidate_portal:confirm")}
                                    >
                                        {t("candidate_portal:confirm")}
                                    </Button>
                                </Row>
                            </Col>
                        </Row>
                    </Form>

                    {showChangePhotoModal && (<ChangePhoto
                        currentPhotoSrc={profileImage?.src}
                        show={showChangePhotoModal}
                        onClose={() => setShowChangePhotoModal(false)}
                        onSubmit={handleChangePhotoSubmit}
                    />)}
                    {showPhotoInfoModal && (<PhotoInfo
                        show={showPhotoInfoModal}
                        onClose={() => setShowPhotoInfoModal(false)}
                    />)}
                    {showAddressModal && (<ChangeContactAddress
                        currentAddress={formData.address}
                        show={true}
                        onClose={() => setShowAddressModal(false)}
                        onSubmit={handleAddressSubmit}
                        editExisting={formData.address?.FullAddress ? true : false}
                    />)}
                    {showAccountModal && (<ChangeBankAccount
                        currentBankAccount={formData.bankAccount}
                        show={true}
                        onClose={() => setShowAccountModal(false)}
                        onSubmit={handleBankAccountSubmit}
                    />)}
                    {showContactPersonModal && (
                        <ContactPersonModal
                            currentItem={formData.contactPerson}
                            show={true}
                            isInEditMode={true}
                            onClose={() => setShowContactPersonModal(false)}
                            onSubmit={handleContactPersonSubmit}
                        />)}
                    {showDocumentModal && (
                        <ChangeDocument
                            currentDocument={editingDocumentIndex !== null ? formData.documents[editingDocumentIndex] : undefined}
                            show={true}
                            onClose={() => { setShowDocumentModal(false); setEditingDocumentIndex(null); }}
                            onSubmit={handleDocumentSubmit}
                        />)}
                </>
            ) : (
                <>
                    <Row>
                        <Col className="col-12 col-xl-4 profile-image-edit-container d-flex justify-content-center ">
                            <div className="position-relative mb-5">
                                <ProfileImage src={profileImage?.src} />
                            </div>
                        </Col>
                        <Col className="col-12 col-xl-8">
                            <Row>
                                <Col className="col-12 col-lg-6">
                                    <ProfileDataNode
                                        label={t("contacts.firstName")}
                                        loading={loadData}
                                    >
                                        {formData.firstName}
                                    </ProfileDataNode>
                                </Col>
                                <Col className="col-12 col-lg-6">
                                    <ProfileDataNode
                                        label={t("contacts.lastName")}
                                        loading={loadData}
                                    >
                                        {formData.lastName}
                                    </ProfileDataNode>

                                </Col>
                            </Row>
                            <Row>
                                <Col className="col-12 col-lg-6">
                                    <ProfileDataNode
                                        label={t("contacts.identificationNumber")}
                                        loading={loadData}
                                    >
                                        {workerInfo?.SSN}
                                    </ProfileDataNode>
                                </Col>
                                <Col className="col-12 col-lg-6">
                                    <ProfileDataNode
                                        label={t("pages:profile.phone")}
                                        loading={loadData}
                                    >
                                        {formData.phone || "-"}
                                    </ProfileDataNode>
                                </Col>
                            </Row>
                            <Row>
                                <ProfileDataNode
                                    label={t("contacts.email")}
                                    loading={loadData}
                                >
                                    {formData.email || "-"}
                                </ProfileDataNode>
                            </Row>

                            <Row>
                                <ProfileDataNode
                                    label={t("pages:profile.homeAddress")}
                                    loading={loadData}
                                >
                                    {renderAddressContent()}
                                </ProfileDataNode>
                            </Row>
                            <Row>
                                <ProfileDataNode
                                    label={t("pages:profile.bankAccount")}
                                    loading={loadData}
                                >
                                    {renderBankAccountContent()}
                                </ProfileDataNode>
                            </Row>
                            <Row>
                                <ProfileDataNode
                                    label={t("contacts.documents")}
                                    loading={loadData}
                                >
                                    {formData.documents.length > 0 ? (
                                        formData.documents.map((document, index) => (
                                            <div key={index} className="mb-2">
                                                <div>{document.TypeName}</div>
                                                <div>{document.Value}</div>
                                                <div>
                                                    {formatDocumentValidTo(document.EndDate) ? `${formatDocumentValidTo(document.EndDate)}` : ""}
                                                </div>
                                            </div>
                                        ))
                                    ) : "-"}
                                </ProfileDataNode>
                            </Row>
                            <Row>
                                <ProfileDataNode
                                    label={t("pages:profile.citizenship")}
                                    loading={loadData}
                                >
                                    {citizenshipCountryName || "-"}
                                </ProfileDataNode>
                            </Row>
                            <Row>
                                <ProfileDataNode
                                    label={t("parts:relatedContact.title")}
                                    loading={loadData}
                                >
                                    {renderContactPersonContent()}
                                    <div className="fs-6 mt-2 fw-normal agreement">{t("candidate_portal:personalInfo.contactPersonSharingTip2")}</div>
                                </ProfileDataNode>
                            </Row>
                            <hr className="mb-4 mt-0" />
                            <Row className="mb-3 mt-5">
                                <div className="fs-5 dataNode-additional-text">
                                    {t("candidate_portal:personalInfo.submittedPleaseWait")}
                                </div>
                            </Row>
                        </Col>
                    </Row>
                </>
            )}
        </div>
    );
}

export default PersonalInfoCard;
